import { useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { useCompare } from '@/context/CompareContext';
import { useCampaign } from '@/context/CampaignContext';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import PerformanceCharts from '../components/PerformanceCharts';

type Tab = 'specs' | 'features' | 'performance';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { products } = useProductCatalog();
  const product = id ? products.find((item) => item.id === id) : undefined;
  const [activeTab, setActiveTab] = useState<Tab>('specs');
  const [pdfLoading, setPdfLoading] = useState(false);
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const { toggle, isSelected, canAdd, selected } = useCompare();
  const { getDiscountedPrice } = useCampaign();
  const navigate = useNavigate();
  const light = theme === 'light';

  const handleDownloadPDF = useCallback(() => {
    setPdfLoading(true);
    setTimeout(() => {
      window.print();
      setPdfLoading(false);
    }, 300);
  }, []);

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const card = light ? 'bg-white border border-gray-200' : 'bg-[#1C2B3A]';
  const text = light ? 'text-gray-900' : 'text-white';
  const muted = light ? 'text-gray-500' : 'text-white/50';
  const faint = light ? 'text-gray-400' : 'text-white/40';
  const breadcrumb = light ? 'text-gray-400' : 'text-white/40';
  const tabInactive = light ? 'text-gray-500 hover:text-gray-900' : 'text-white/50 hover:text-white';
  const tabBorder = light ? 'border-gray-200' : 'border-white/10';
  const specRow = light ? 'bg-gray-50 border border-gray-100' : 'bg-[#1C2B3A]';
  const specKey = light ? 'text-gray-500' : 'text-white/50';
  const specVal = light ? 'text-gray-900' : 'text-white';
  const featureRow = light ? 'bg-gray-50 border border-gray-100' : 'bg-[#1C2B3A]';
  const relatedCard = light ? 'bg-white border border-gray-200 hover:bg-gray-50' : 'bg-[#1C2B3A] hover:bg-[#232F3E]';
  const actionBorder = light ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-white/30 text-white hover:bg-white/10';
  const pdfBtn = light ? 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200' : 'bg-white/10 border border-white/20 text-white hover:bg-white/20';

  if (!product) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <div className={`text-center ${text}`}>
          <i className="ri-error-warning-line text-5xl mb-4 block" style={{ color: 'var(--accent)' }}></i>
          <p className="text-xl font-bold mb-4">Product not found</p>
          <Link to="/products" className="hover:underline cursor-pointer" style={{ color: 'var(--accent)' }}>Back to Products</Link>
        </div>
      </div>
    );
  }

  const { price: discountedPrice, campaign } = getDiscountedPrice(product.id, product.category, product.price);
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);
  const categoryLabel = product.category === 'lithium' ? 'Lithium LiFePO4' : product.category === 'agm' ? 'AGM Series' : 'Gel Series';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    brand: { '@type': 'Brand', name: 'Stingray' },
    sku: product.id,
    category: `Marine Batteries > ${categoryLabel}`,
    offers: { '@type': 'Offer', priceCurrency: 'USD', price: product.price, availability: 'https://schema.org/InStock', seller: { '@type': 'Organization', name: 'Authorized Stingray Dealers' } },
    additionalProperty: Object.entries(product.specs).map(([name, value]) => ({ '@type': 'PropertyValue', name, value })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://stingray-marine.com/' },
      { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://stingray-marine.com/products' },
      { '@type': 'ListItem', position: 3, name: product.name, item: `https://stingray-marine.com/products/${product.id}` },
    ],
  };

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title={`${product.name} | ${categoryLabel} Marine Battery | Stingray`}
        description={`${product.name} — ${product.tagline}. ${product.capacity}Ah, ${product.voltage}V, ${product.weight} lbs. ${product.description.slice(0, 100)}...`}
        keywords={`${product.name}, ${categoryLabel} marine battery, ${product.capacity}ah marine battery, Stingray ${product.category} battery`}
        canonical={`https://stingray-marine.com/products/${product.id}`}
        ogType="product"
        schema={[productSchema, breadcrumbSchema]}
      />
      <Navbar />

      <style>{`
        @media print {
          header, footer, nav, .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          .print-section { break-inside: avoid; }
        }
      `}</style>

      <div className={`w-full px-6 md:px-10 pt-28 pb-20 ${text}`}>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${breadcrumb} mb-8`}>
          <Link to="/" className="hover:text-[var(--accent)] cursor-pointer transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/products" className="hover:text-[var(--accent)] cursor-pointer transition-colors">Products</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className={muted}>{product.name}</span>
        </nav>

        {/* Main Product Section */}
        <article className="flex flex-col lg:flex-row gap-12 mb-16 print-section">
          {/* Image */}
          <div className="flex-1">
            <div className={`relative w-full h-[280px] sm:h-[420px] rounded-lg overflow-hidden ${card}`}>
              <img
                src={product.image}
                alt={`${product.name} — ${categoryLabel} marine battery`}
                title={`${product.name} | Stingray`}
                className="w-full h-full object-cover object-top"
              />
              {settings.productBadges && product.badge && (
                <span className="absolute top-4 left-4 text-white text-xs font-bold px-4 py-1.5 rounded-full" style={{ backgroundColor: 'var(--accent)' }}>
                  {product.badge}
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            {settings.productBadges && (
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
              >
                {categoryLabel}
              </span>
            )}
            <h1 className={`text-3xl md:text-4xl font-black mb-2 ${text}`}>{product.name}</h1>
            <p className={`text-base mb-4 ${muted}`}>{product.tagline}</p>
            <p className={`text-sm leading-relaxed mb-6 ${muted}`}>{product.description}</p>

            {/* Quick Specs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Capacity', value: `${product.capacity}Ah` },
                { label: 'Voltage', value: `${product.voltage}V` },
                { label: 'Weight', value: `${product.weight} lbs` },
                { label: 'Cycles', value: `${product.performance.cycleLife.toLocaleString()}+` },
              ].map((s) => (
                <div key={s.label} className={`rounded-lg p-3 text-center ${card}`}>
                  <p className="text-lg font-black" style={{ color: 'var(--accent)' }}>{s.value}</p>
                  <p className={`text-xs mt-0.5 ${muted}`}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Efficiency badge */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-3 rounded-lg ${card}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                  <i className="ri-flashlight-line text-lg" style={{ color: 'var(--accent)' }}></i>
                </div>
                <div>
                  <p className={`text-xs font-semibold ${muted}`}>Round-trip Efficiency</p>
                  <p className={`text-lg font-black ${text}`}>{product.performance.efficiency}%</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                  <i className="ri-scales-3-line text-lg" style={{ color: 'var(--accent)' }}></i>
                </div>
                <div>
                  <p className={`text-xs font-semibold ${muted}`}>Energy Density</p>
                  <p className={`text-lg font-black ${text}`}>{product.performance.energyDensity} Wh/kg</p>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              {campaign ? (
                <>
                  <div className="flex flex-col">
                    <span className={`text-sm line-through ${faint}`}>${product.price.toLocaleString()} Reference MSRP</span>
                    <span className="text-4xl font-black" style={{ color: 'var(--accent)' }}>${discountedPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {settings.productBadges && (
                      <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full">
                        <i className="ri-megaphone-line"></i>
                        {campaign.badgeLabel || campaign.name}
                      </span>
                    )}
                    <span className="text-emerald-400 text-sm font-bold">
                      {campaign.discountType === 'percentage'
                        ? `Save ${campaign.discountValue}% — $${(product.price - discountedPrice).toLocaleString()} off`
                        : `Save $${campaign.discountValue}`}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-4xl font-black" style={{ color: 'var(--accent)' }}>${product.price.toLocaleString()}</p>
                  <span className={`text-sm ${faint}`}>Reference MSRP</span>
                </>
              )}
            </div>
            <p className={`text-xs leading-relaxed mb-6 ${faint}`}>
              Pricing shown is reference MSRP. Final retail pricing, availability, and fulfillment may vary by dealer, region, or sales platform.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 no-print">
              <Link
                to="/contact"
                className="flex-1 text-white font-bold py-3.5 rounded-md transition-colors text-center cursor-pointer whitespace-nowrap text-sm tracking-wide uppercase"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                Find a Dealer
              </Link>
              <Link
                to="/dealer"
                className={`flex-1 border font-bold py-3.5 rounded-md transition-colors text-center cursor-pointer whitespace-nowrap text-sm tracking-wide uppercase ${actionBorder}`}
              >
                Dealer Login
              </Link>
              <button
                onClick={handleDownloadPDF}
                disabled={pdfLoading}
                className={`flex items-center justify-center gap-2 px-5 py-3.5 rounded-md font-bold text-sm transition-colors cursor-pointer whitespace-nowrap ${pdfBtn}`}
                title="Download Product Datasheet as PDF"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={pdfLoading ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-line'}></i>
                </div>
                {pdfLoading ? 'Preparing...' : 'PDF'}
              </button>
            </div>

            {/* Compare action */}
            <div className={`flex items-center gap-3 mt-3 p-3 rounded-lg no-print ${light ? 'bg-gray-50 border border-gray-100' : 'bg-[#0D1B2A]/60 border border-white/5'}`}>
              <button
                onClick={() => product && toggle(product)}
                disabled={!isSelected(product?.id || '') && !canAdd}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                  isSelected(product?.id || '')
                    ? 'text-white'
                    : canAdd
                    ? light
                      ? 'bg-white border-gray-200 text-gray-700'
                      : 'bg-white/10 border-white/20 text-white'
                    : light
                    ? 'bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed'
                    : 'bg-white/5 border-white/5 text-white/20 cursor-not-allowed'
                }`}
                style={isSelected(product?.id || '') ? { backgroundColor: 'var(--accent)', borderColor: 'var(--accent)' } : {}}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={isSelected(product?.id || '') ? 'ri-check-line' : 'ri-bar-chart-grouped-line'}></i>
                </div>
                {isSelected(product?.id || '')
                  ? 'Added to Compare'
                  : canAdd
                  ? 'Add to Compare'
                  : 'Compare Full (3/3)'}
              </button>

              {selected.length >= 2 && (
                <button
                  onClick={() => navigate(`/products/compare?ids=${selected.map((p) => p.id).join(',')}`)}
                  className="flex items-center gap-1.5 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap transition-colors"
                  style={{ color: 'var(--accent)' }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                  View Comparison ({selected.length})
                </button>
              )}

              {selected.length > 0 && selected.length < 2 && (
                <span className={`text-xs ${light ? 'text-gray-400' : 'text-white/30'}`}>
                  Select {2 - selected.length} more to compare
                </span>
              )}
            </div>
          </div>
        </article>

        {/* Tabs */}
        <section className="mb-16">
          <div className={`overflow-x-auto border-b mb-6 ${tabBorder}`}>
            <div className="flex w-max min-w-full gap-0">
              {([
              { key: 'specs', label: 'Technical Specs', icon: 'ri-list-check-2' },
              { key: 'features', label: 'Key Features', icon: 'ri-star-line' },
              { key: 'performance', label: 'Performance', icon: 'ri-bar-chart-line' },
            ] as { key: Tab; label: string; icon: string }[]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? 'border-transparent'
                    : `border-transparent ${tabInactive}`
                }`}
                style={activeTab === tab.key ? { borderBottomColor: 'var(--accent)', color: 'var(--accent)' } : {}}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'specs' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 print-section">
              {Object.entries(product.specs).map(([key, val]) => (
                <div key={key} className={`flex items-center justify-between rounded-lg px-5 py-3 ${specRow}`}>
                  <span className={`text-sm ${specKey}`}>{key}</span>
                  <strong className={`font-semibold text-sm ${specVal}`}>{val}</strong>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'features' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print-section">
              {product.features.map((f, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-lg px-5 py-3 ${featureRow}`}>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-base" style={{ color: 'var(--accent)' }}></i>
                  </div>
                  <span className={`text-sm ${text}`}>{f}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className={text}>
              <PerformanceCharts product={product} />
            </div>
          )}
        </section>

        {/* Related Products */}
        {related.length > 0 && (
          <section className="no-print">
            <h2 className={`text-2xl font-black mb-6 ${text}`}>More in {categoryLabel}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className={`group rounded-lg overflow-hidden transition-colors cursor-pointer ${relatedCard}`}
                >
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={p.image}
                      alt={`${p.name} marine battery`}
                      title={p.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <p className={`font-bold text-sm group-hover:text-[var(--accent)] transition-colors ${text}`}>{p.name}</p>
                    <p className="font-black mt-1" style={{ color: 'var(--accent)' }}>${p.price.toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}
